/**
 * S013 Playwright-only empty-fixture signal.
 *
 * The selection is narrowly gated:
 *
 * 1. `import.meta.env.MODE` must equal `e2e`. Non-E2E execution returns
 *    before reading the window value.
 * 2. The window global `window.__INDEEP_E2E_FIXTURE__` must equal the
 *    closed enum value `S013_EMPTY_FIXTURE_SIGNAL` ('empty').
 *
 * The closed enum is intentionally small: `loaded | empty`. Anything else
 * falls through to the production-default loaded factory. No URL state,
 * no production API, and no fixture other than the canonical empty
 * composition can be injected. The seam exists only to give Playwright
 * a deterministic composition switch for the S013 browser spec.
 *
 * **Required invariant:** MODE !== "e2e" ⇒ window.__INDEEP_E2E_FIXTURE__
 * is not accessed. It is not enough that an accessed value is ignored.
 */

export const S013_EMPTY_FIXTURE_SIGNAL = 'empty' as const;
export const S013_LOADED_FIXTURE_SIGNAL = 'loaded' as const;

export type S013E2EFixtureSignal =
  | typeof S013_EMPTY_FIXTURE_SIGNAL
  | typeof S013_LOADED_FIXTURE_SIGNAL;

export interface E2EChatListFixtureSelection {
  readonly e2eMode: boolean;
  readonly signal: string | null;
  readonly resolvedEmpty: boolean;
}

/**
 * Canonical non-E2E selection used when the resolver determines that
 * we are not in E2E mode. This frozen singleton prevents any window
 * read in the default path.
 */
const NON_E2E_SELECTION: E2EChatListFixtureSelection = Object.freeze({
  e2eMode: false,
  signal: null,
  resolvedEmpty: false,
});

/**
 * Narrow local type for Vite's import.meta.env.
 * This bounded approach avoids changing the ambient type environment
 * of unrelated files.
 */
interface ViteImportMeta extends ImportMeta {
  readonly env?: {
    readonly MODE?: unknown;
  };
}

function readViteMode(): string | undefined {
  const mode = (import.meta as ViteImportMeta).env?.MODE;
  return typeof mode === 'string' ? mode : undefined;
}

function readE2EChatListFixtureSignalFromWindow(): string | null {
  if (typeof window === 'undefined') return null;
  const value = (window as unknown as { __INDEEP_E2E_FIXTURE__?: unknown })
    .__INDEEP_E2E_FIXTURE__;
  return typeof value === 'string' ? value : null;
}

/**
 * Pure resolver: given explicit mode and signal values, return the
 * normalized selection. No window access occurs here.
 *
 * @param e2eMode - Whether we are in E2E mode (caller determines this)
 * @param signal - The raw signal value from window (or null if not read)
 */
export function resolveE2EChatListFixtureSelection(
  e2eMode: boolean,
  signal: unknown,
): E2EChatListFixtureSelection {
  if (!e2eMode) {
    return NON_E2E_SELECTION;
  }

  const normalizedSignal =
    typeof signal === 'string' ? signal : null;

  return Object.freeze({
    e2eMode: true,
    signal: normalizedSignal,
    resolvedEmpty:
      normalizedSignal === S013_EMPTY_FIXTURE_SIGNAL,
  });
}

/**
 * Read the E2E fixture selection with production-inert default behavior.
 *
 * Production behavior:
 * - production/development/test mode: no global read, returns NON_E2E_SELECTION
 * - e2e mode + absent/unknown/loaded signal: loaded fixture remains default
 * - e2e mode + empty signal: empty fixture selected
 *
 * **Required invariant:** MODE !== "e2e" ⇒ window.__INDEEP_E2E_FIXTURE__
 * is not accessed.
 */
export function readE2EChatListFixtureSelection():
  E2EChatListFixtureSelection {
  const e2eMode = readViteMode() === 'e2e';

  if (!e2eMode) {
    return NON_E2E_SELECTION;
  }

  return resolveE2EChatListFixtureSelection(
    true,
    readE2EChatListFixtureSignalFromWindow(),
  );
}
